/**
 * SharedListsTab Component
 *
 * Tab content for "Shared" -- shows lists where the user is an accepted member
 * but not the owner. Includes search bar and shows owner name.
 */

import { listsQueryOptions } from '@/api/queries/lists';
import { SearchBar } from '@/components/forms/search-bar';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useQuery } from '@tanstack/react-query';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, Text, View } from 'react-native';
import { ListCard } from './list-card';

export function SharedListsTab() {
  const theme = useAppTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  const {
    data: listsData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery(
    listsQueryOptions({
      per_page: 50,
      scope: 'shared',
      search: debouncedSearch || undefined,
    })
  );

  const lists = listsData?.items ?? [];

  const handleListPress = (listId: string) => {
    router.push({
      pathname: '/(root)/lists/[id]',
      params: { id: listId },
    });
  };

  return (
    <ScrollView
      className="flex-1 px-4 pt-2"
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={isRefetching}
          onRefresh={() => refetch()}
          tintColor={theme.colors.primary}
        />
      }
      contentContainerStyle={{ paddingBottom: 100 }}
    >
      <SearchBar
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholder="Search shared lists..."
      />

      {isLoading ? (
        <View className="items-center justify-center py-12">
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      ) : lists.length === 0 ? (
        <View className="items-center justify-center py-12">
          <Ionicons name="people-outline" size={48} className="text-muted-foreground" />
          <Text className="text-base font-medium mt-4 text-center text-muted-foreground">
            {debouncedSearch ? 'No matching lists' : 'No lists shared with you yet'}
          </Text>
          {!debouncedSearch && (
            <Text className="text-sm mt-2 text-center text-muted-foreground/70">
              When friends share their lists with you, they will appear here.
            </Text>
          )}
        </View>
      ) : (
        <View>
          {lists.map((list) => (
            <ListCard
              key={list.id}
              list={list}
              onPress={() => handleListPress(list.id)}
              showOwner
            />
          ))}
        </View>
      )}
    </ScrollView>
  );
}
